/**
 * Speaking LLM Client - 统一使用 OpenRouter
 * 所有调用都通过 openrouter.ts 中的函数完成
 */
import { LLMOrchestrator } from '../../services/llm/orchestrator';
import { buildInvoker } from '../../services/llm/providerFactory';
import { readLLMTimeouts, getLLMLogger } from '../../services/llm/config';
import { callOpenRouter, callOpenRouterWithAudio } from '../../services/llm/openrouter';
import { isPublicDeploy } from '../../config/features';

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// 目标采样率：16kHz 语音识别标准，平衡质量和体积
// 50秒录音：16kHz × 2字节 × 50 = 1.6MB，Base64 后约 2.1MB
const TARGET_SAMPLE_RATE = 16000;

/**
 * 重采样 AudioBuffer 到目标采样率
 */
async function resampleAudioBuffer(audioBuffer: AudioBuffer, targetRate: number): Promise<AudioBuffer> {
  if (audioBuffer.sampleRate === targetRate) {
    return audioBuffer;
  }
  
  const offlineCtx = new OfflineAudioContext(
    1, // 单声道
    Math.ceil(audioBuffer.duration * targetRate),
    targetRate
  );
  
  const source = offlineCtx.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(offlineCtx.destination);
  source.start(0);
  
  return await offlineCtx.startRendering();
}

/**
 * 将任意音频 Blob 转换为 WAV 格式
 * OpenRouter 只支持 wav 和 mp3，浏览器录制的 webm 需要转换
 * 会自动降采样到 16kHz 以减少文件体积（Netlify 限制 1MB）
 */
async function convertToWav(blob: Blob): Promise<{ base64: string; mimeType: string }> {
  const audioContext = new AudioContext();
  const arrayBuffer = await blob.arrayBuffer();
  
  try {
    // 克隆 arrayBuffer，因为 decodeAudioData 会 detach 它
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
    
    // 检查解码结果
    const channelData = audioBuffer.getChannelData(0);
    const maxAmplitude = Math.max(...Array.from(channelData.slice(0, 1000)).map(Math.abs));
    console.log('[audio] decode check:', {
      channels: audioBuffer.numberOfChannels,
      length: audioBuffer.length,
      duration: audioBuffer.duration,
      sampleRate: audioBuffer.sampleRate,
      maxAmplitudeFirst1000: maxAmplitude,
    });
    
    // 如果解码后几乎没有声音，说明解码失败
    if (maxAmplitude < 0.001) {
      console.warn('[audio] decoded audio appears silent, decode may have failed');
    }
    
    // 重采样到 16kHz（减少文件体积约 3 倍）
    const resampledBuffer = await resampleAudioBuffer(audioBuffer, TARGET_SAMPLE_RATE);
    console.log('[audio] resampled:', {
      originalRate: audioBuffer.sampleRate,
      targetRate: TARGET_SAMPLE_RATE,
      originalLength: audioBuffer.length,
      resampledLength: resampledBuffer.length,
    });
    
    // 转换为 WAV
    const wavBuffer = encodeWav(resampledBuffer);
    const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });
    const base64 = await blobToBase64(wavBlob);
    
    const base64SizeKB = Math.round(base64.length / 1024);
    console.log('[audio] converted to wav:', {
      originalSize: blob.size,
      wavSize: wavBlob.size,
      base64SizeKB,
      durationSec: audioBuffer.duration,
    });
    
    // 检查大小限制：
    // - 公网版（Netlify）请求体限制更严格，保持 5MB
    // - 私有版（Cloudflare）允许更长录音，提升到 20MB（约 3-4 分钟）
    const MAX_BASE64_KB = isPublicDeploy ? 5 * 1024 : 20 * 1024;
    if (base64SizeKB > MAX_BASE64_KB) {
      const maxDuration = Math.floor(MAX_BASE64_KB / (TARGET_SAMPLE_RATE * 2 / 1024 * 1.33));
      throw new Error(`录音过长（${Math.round(audioBuffer.duration)}秒），请控制在 ${maxDuration} 秒以内`);
    }
    
    // 调试：下载 WAV 文件检查
    if (typeof window !== 'undefined' && (window as any).__debug_download_wav) {
      const url = URL.createObjectURL(wavBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'debug-audio.wav';
      a.click();
      URL.revokeObjectURL(url);
    }
    
    return { base64, mimeType: 'audio/wav' };
  } finally {
    await audioContext.close();
  }
}

/**
 * 将 AudioBuffer 编码为 WAV 格式
 */
function encodeWav(audioBuffer: AudioBuffer): ArrayBuffer {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  
  // 获取所有通道数据并混合为单声道
  let samples: Float32Array;
  if (numChannels === 1) {
    samples = audioBuffer.getChannelData(0);
  } else {
    // 混合为单声道
    samples = new Float32Array(audioBuffer.length);
    for (let i = 0; i < audioBuffer.length; i++) {
      let sum = 0;
      for (let ch = 0; ch < numChannels; ch++) {
        sum += audioBuffer.getChannelData(ch)[i];
      }
      samples[i] = sum / numChannels;
    }
  }
  
  const dataLength = samples.length * (bitDepth / 8);
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);
  
  // WAV 文件头
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, format, true);
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 1 * (bitDepth / 8), true); // byte rate
  view.setUint16(32, 1 * (bitDepth / 8), true); // block align
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true);
  
  // 写入采样数据
  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    offset += 2;
  }
  
  return buffer;
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

/**
 * 文本 LLM 调用（用于整理想法、生成例句等）
 * @deprecated 参数 apiKey 和 model 已不使用，保留仅为兼容
 */
export async function callGeminiText(_apiKey: string, prompt: string, _model?: string): Promise<string> {
  const { totalTimeoutMs } = readLLMTimeouts();
  const logger = getLLMLogger();

  const orchestrator = new LLMOrchestrator<string>(
    [
      buildInvoker('openrouter', async () => {
        const text = await callOpenRouter(prompt);
        if (text) return text;
        throw new Error('OpenRouter 返回空');
      }, 15000),
    ],
    { totalTimeoutMs: totalTimeoutMs ?? 20000, logger }
  );

  const result = await orchestrator.run();
  return result.data;
}

export interface AudioAnalysisParams {
  apiKey: string;
  blob: Blob;
  prompt?: string;
  mimeType?: string;
}

/**
 * 音频 LLM 调用（用于转写和反馈）
 * 使用 OpenRouter 的 input_audio 格式
 * 会自动将 webm 等格式转换为 wav
 * @deprecated 参数 apiKey 已不使用，保留仅为兼容
 */
export async function callGeminiAudio(params: AudioAnalysisParams): Promise<string> {
  const { blob, prompt } = params;
  const { totalTimeoutMs } = readLLMTimeouts();
  const logger = getLLMLogger();

  // 将音频转换为 WAV 格式（OpenRouter 只支持 wav/mp3）
  const { base64: audioBase64, mimeType } = await convertToWav(blob);

  const defaultPrompt = 'Transcribe the audio and provide concise fluency / grammar / vocab feedback.';

  const orchestrator = new LLMOrchestrator<string>(
    [
      buildInvoker('openrouter-audio', async () => {
        const text = await callOpenRouterWithAudio({
          audioBase64,
          mimeType,
          prompt: prompt ?? defaultPrompt,
        });
        if (text) return text;
        throw new Error('OpenRouter 音频分析返回空');
      }, 30000),
    ],
    { totalTimeoutMs: totalTimeoutMs ?? 45000, logger }
  );

  const result = await orchestrator.run();
  return result.data;
}
